import { type ComplianceProvider, type ComplianceSubject, useGetComplianceProvidersQuery } from "@ots-frontend/ots-data";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

/** WaterGo's rule: a subject has compliance to show when any provider (program) applies to it. */
export const hasCompliancePrograms = (providers: ComplianceProvider[] | undefined): boolean =>
  !!providers?.some((provider) => !!provider?.provider);

const subjectKey = (subject: ComplianceSubject) => `${subject.kind}:${subject.tenantCode ?? ""}:${subject.id}`;

/** How many subjects are checked at once; older ones drop off as the person moves on. */
const MAX_PROBES = 20;

const Probe = ({ subject, onResult }: { subject: ComplianceSubject; onResult: (key: string, available: boolean) => void }) => {
  const { data, isSuccess, isError } = useGetComplianceProvidersQuery(subject);
  useEffect(() => {
    // As in WaterGo, a failed lookup still offers Compliance rather than hiding it.
    if (isError) onResult(subjectKey(subject), true);
    else if (isSuccess) onResult(subjectKey(subject), hasCompliancePrograms(data));
  }, [data, isError, isSuccess, onResult, subject]);
  return null;
};

export type ComplianceAvailability = {
  /** True when the subject has compliance, false when it has none, undefined while it is being checked. */
  isAvailable: (subject: ComplianceSubject) => boolean | undefined;
  /** The lookups themselves; render them anywhere (they draw nothing). */
  probes: ReactNode;
};

/**
 * Whether tickets have compliance to show, asked as they come up (WaterGo's `ComplianceAvailability`):
 * `isAvailable` starts a lookup for a subject it has not seen and answers undefined until it returns.
 */
export const useComplianceAvailability = (): ComplianceAvailability => {
  const [results, setResults] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [subjects, setSubjects] = useState<ComplianceSubject[]>([]);
  const asked = useRef(new Set<string>());

  const onResult = useCallback((key: string, available: boolean) => setResults((current) =>
    current.get(key) === available ? current : new Map(current).set(key, available)), []);

  const isAvailable = (subject: ComplianceSubject) => {
    const key = subjectKey(subject);
    if (results.has(key)) return results.get(key);
    if (!asked.current.has(key)) {
      asked.current.add(key);
      // Asked while rendering a menu; start the lookup once that render is done.
      queueMicrotask(() => setSubjects((current) => [...current.slice(-(MAX_PROBES - 1)), subject]));
    }
    return undefined;
  };

  return {
    isAvailable,
    probes: subjects.map((subject) => <Probe key={subjectKey(subject)} subject={subject} onResult={onResult}/>),
  };
};
